import { state, updateState } from '../state/store.js';
import {
  getCollections,
  getDocumentsInCollection,
  getDocumentationChunks,
  searchDocumentation,
} from '../api/client.js';

// DOM Elements
let documentationList;
let docCount;
let docSearch;
let semanticToggle;
let collectionSelect;
let documentSelect;
let selectAllDocsBtn;
let clearDocSelectionBtn;

export function initDocumentation() {
  // Get DOM elements
  documentationList = document.getElementById('documentationList');
  docCount = document.getElementById('docCount');
  docSearch = document.getElementById('docSearch');
  semanticToggle = document.getElementById('semanticToggle');
  collectionSelect = document.getElementById('collectionSelect');
  documentSelect = document.getElementById('documentSelect');
  selectAllDocsBtn = document.getElementById('selectAllDocs');
  clearDocSelectionBtn = document.getElementById('clearDocSelection');

  // Documentation search
  if (docSearch) {
    let searchTimeout;
    docSearch.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        handleDocumentationSearch(e.target.value);
      }, 300);
    });
  }

  // Segmented search-mode toggle (Text / KI-Suche buttons)
  if (semanticToggle) {
    const modeBtns = semanticToggle.querySelectorAll('.search-mode-btn');
    modeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const isKI = btn.dataset.mode === 'ki';
        if (isKI === state.semanticSearchEnabled) return; // already active
        updateState({ semanticSearchEnabled: isKI });
        modeBtns.forEach((b) => b.classList.toggle('active', b === btn));

        // Update placeholder to hint at the mode
        if (docSearch) {
          docSearch.placeholder = isKI
            ? 'KI-Suche: Frage oder Stichwort eingeben...'
            : 'Dokumentation durchsuchen...';
        }

        // Re-run search if there's a query
        if (docSearch && docSearch.value.trim()) {
          handleDocumentationSearch(docSearch.value);
        }
      });
    });
  }

  // Collection selector
  if (collectionSelect) {
    collectionSelect.addEventListener('change', async (e) => {
      updateState({ 
        currentCollection: e.target.value, 
        allDocChunks: [],
        availableDocuments: [],
        selectedDocuments: []
      });
      if (docSearch) docSearch.value = '';
      await loadDocumentsInCollection();
      await loadDocumentation();
    });
  }

  // Document selector
  if (documentSelect) {
    documentSelect.addEventListener('change', (e) => {
      const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
      updateState({ selectedDocuments: selectedOptions });
      if (docSearch) docSearch.value = '';
      loadDocumentation();
    });
  }

  // Select all documents button
  if (selectAllDocsBtn) {
    selectAllDocsBtn.addEventListener('click', () => {
      if (documentSelect) {
        const allOptions = Array.from(documentSelect.options);
        allOptions.forEach(opt => opt.selected = true);
        updateState({ selectedDocuments: state.availableDocuments });
        loadDocumentation();
      }
    });
  }

  // Clear document selection button
  if (clearDocSelectionBtn) {
    clearDocSelectionBtn.addEventListener('click', () => {
      if (documentSelect) {
        const allOptions = Array.from(documentSelect.options);
        allOptions.forEach(opt => opt.selected = false);
        updateState({ selectedDocuments: [] });
        loadDocumentation();
      }
    });
  }
}

export async function loadCollections() {
  try {
    const data = await getCollections();
    updateState({ availableCollections: data.collections });

    // Populate collection dropdown
    if (collectionSelect) {
      collectionSelect.innerHTML = '';

      if (state.availableCollections.length === 0) {
        collectionSelect.innerHTML = '<option value="">Keine Sammlungen verfügbar</option>';
        return;
      }

      state.availableCollections.forEach((collection) => {
        const option = document.createElement('option');
        option.value = collection.name;
        option.textContent = `${collection.label || collection.name} (${collection.count} Abschnitte)`;
        if (collection.name === state.currentCollection) {
          option.selected = true;
        }
        collectionSelect.appendChild(option);
      });

      // Set first collection as default if current is not available
      if (!state.availableCollections.find((c) => c.name === state.currentCollection)) {
        updateState({ currentCollection: state.availableCollections[0].name });
      }
    }
  } catch (error) {
    console.error('Error loading collections:', error);
    if (collectionSelect) {
      collectionSelect.innerHTML = '<option value="">Fehler beim Laden</option>';
    }
  }
}

export async function loadDocumentsInCollection() {
  try {
    if (!state.currentCollection) return;

    if (documentSelect) {
      documentSelect.innerHTML = '<option value="">Dokumente werden geladen...</option>';
    }

    const data = await getDocumentsInCollection(state.currentCollection);
    updateState({ availableDocuments: data.documents });

    // Populate document dropdown
    if (documentSelect) {
      documentSelect.innerHTML = '';

      if (state.availableDocuments.length === 0) {
        documentSelect.innerHTML = '<option value="">Keine Dokumente verfügbar</option>';
        return;
      }

      state.availableDocuments.forEach((doc) => {
        const option = document.createElement('option');
        option.value = doc;
        option.textContent = doc;
        documentSelect.appendChild(option);
      });
    }
  } catch (error) {
    console.error('Error loading documents:', error);
    if (documentSelect) {
      documentSelect.innerHTML = '<option value="">Fehler beim Laden</option>';
    }
  }
}

const CHUNK_PAGE_SIZE = 200;
let currentOffset = 0;
let totalChunks = 0;

export async function loadDocumentation() {
  try {
    docCount.textContent = 'Dokumentation wird geladen...';
    documentationList.innerHTML = '<div class="loading-spinner"><div class="spinner"></div></div>';

    currentOffset = 0;
    const data = await getDocumentationChunks(state.currentCollection, state.selectedDocuments, CHUNK_PAGE_SIZE, 0);
    totalChunks = data.total_chunks;
    updateState({ allDocChunks: data.chunks });

    updateDocCount();
    displayDocumentation(state.allDocChunks);
    appendLoadMoreButton(data.has_more);
  } catch (error) {
    docCount.textContent = 'Fehler beim Laden';
    {
      const _e = document.createElement("div");
      _e.className = "error-message";
      _e.textContent = `Fehler: ${error.message}`;
      documentationList.replaceChildren(_e);
    }
  }
}

async function loadMoreChunks() {
  const btn = documentationList.querySelector('.btn-load-more');
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Wird geladen...';
  }
  try {
    currentOffset += CHUNK_PAGE_SIZE;
    const data = await getDocumentationChunks(state.currentCollection, state.selectedDocuments, CHUNK_PAGE_SIZE, currentOffset);
    const merged = [...state.allDocChunks, ...data.chunks];
    updateState({ allDocChunks: merged });

    // Remove old load-more button before re-rendering
    if (btn) btn.remove();

    // Append new chunks instead of re-rendering everything
    data.chunks.forEach((chunk) => {
      documentationList.appendChild(createDocumentationCard(chunk));
    });

    updateDocCount();
    appendLoadMoreButton(data.has_more);
  } catch (error) {
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Erneut versuchen';
    }
  }
}

function updateDocCount() {
  const loaded = state.allDocChunks.length;
  const docFilter = state.selectedDocuments.length > 0
    ? ` (${state.selectedDocuments.length} Dokument${state.selectedDocuments.length > 1 ? 'e' : ''} gefiltert)`
    : '';
  const ofTotal = loaded < totalChunks ? ` von ${totalChunks}` : '';
  docCount.textContent = `${loaded}${ofTotal} Abschnitte${docFilter}`;
}

function appendLoadMoreButton(hasMore) {
  // Remove existing button if any
  const existing = documentationList.querySelector('.btn-load-more');
  if (existing) existing.remove();

  if (!hasMore) return;

  const remaining = totalChunks - state.allDocChunks.length;
  const btn = document.createElement('button');
  btn.className = 'btn-load-more';
  btn.textContent = `Weitere ${Math.min(remaining, CHUNK_PAGE_SIZE)} von ${remaining} laden`;
  btn.addEventListener('click', loadMoreChunks);
  documentationList.appendChild(btn);
}

function displayDocumentation(chunks) {
  documentationList.innerHTML = '';

  if (chunks.length === 0) {
    documentationList.innerHTML = '<div class="no-results">Keine Abschnitte gefunden. Versuchen Sie, die Dokumentenauswahl anzupassen.</div>';
    return;
  }

  chunks.forEach((chunk) => {
    const docCard = createDocumentationCard(chunk);
    documentationList.appendChild(docCard);
  });
}

function createDocumentationCard(chunk) {
  const card = document.createElement('div');
  card.className = 'doc-card';
  card.dataset.chunkId = chunk.id;

  const header = document.createElement('div');
  header.className = 'doc-header';
  const title = document.createElement('div');
  title.className = 'doc-section-title';
  title.textContent = chunk.document_name || 'Dokumentabschnitt';
  header.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.className = 'doc-parent-chapter';
  subtitle.textContent = chunk.section ? `Abschnitt ${chunk.section}` : 'Abschnitt';
  header.appendChild(subtitle);

  const content = document.createElement('div');
  content.className = 'doc-content';
  content.textContent = chunk.content;

  const footer = document.createElement('div');
  footer.className = 'doc-footer';

  const sectionLabel = chunk.section || '?';
  let footerHTML = `<span class="doc-chunk-id">${sectionLabel}</span>`;

  // Show relevance score if available (from semantic search)
  if (chunk.distance !== undefined) {
    // ChromaDB uses cosine distance (0-2), convert to similarity percentage
    const similarity = 1 - chunk.distance / 2;
    const relevance = Number(Math.max(0, Math.min(100, similarity * 100)).toFixed(1));
    const relevanceClass = relevance >= 70 ? 'relevance-high' : relevance >= 45 ? 'relevance-medium' : 'relevance-low';
    footerHTML += `<span class="doc-relevance ${relevanceClass}">Relevanz: ${relevance.toFixed(1)}%</span>`;
  }

  footerHTML += '<span class="doc-expand-icon" aria-hidden="true">&#9660;</span>';
  footer.innerHTML = footerHTML;
  footer.setAttribute('role', 'button');
  footer.setAttribute('tabindex', '0');
  footer.setAttribute('aria-expanded', 'false');
  footer.setAttribute('aria-label', 'Dokumentabschnitt ein- oder ausklappen');

  card.appendChild(header);
  card.appendChild(content);
  card.appendChild(footer);

  // Add expand/collapse functionality
  content.style.maxHeight = '150px';
  content.style.overflow = 'hidden';

  let isExpanded = false;
  const toggleExpanded = () => {
    isExpanded = !isExpanded;
    footer.setAttribute('aria-expanded', String(isExpanded));
    if (isExpanded) {
      content.style.maxHeight = 'none';
      card.classList.add('expanded');
    } else {
      content.style.maxHeight = '150px';
      card.classList.remove('expanded');
    }
  };

  footer.addEventListener('click', toggleExpanded);
  footer.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  });

  return card;
}

function filterDocumentation(searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    displayDocumentation(state.allDocChunks);
    updateDocCount();
    appendLoadMoreButton(state.allDocChunks.length < totalChunks);
    return;
  }

  const term = searchTerm.toLowerCase();
  const filtered = state.allDocChunks.filter(
    (chunk) =>
      (chunk.section || '').toLowerCase().includes(term) ||
      (chunk.content || '').toLowerCase().includes(term) ||
      (chunk.document_name || '').toLowerCase().includes(term)
  );

  displayDocumentation(filtered);
  const partial = state.allDocChunks.length < totalChunks ? ` (${state.allDocChunks.length} geladen)` : '';
  docCount.textContent = `${filtered.length} von ${state.allDocChunks.length} Abschnitten${partial}`;
}

async function handleDocumentationSearch(searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    displayDocumentation(state.allDocChunks);
    updateDocCount();
    appendLoadMoreButton(state.allDocChunks.length < totalChunks);
    return;
  }

  if (state.semanticSearchEnabled) {
    await performSemanticSearch(searchTerm);
  } else {
    filterDocumentation(searchTerm);
  }
}

async function performSemanticSearch(query) {
  try {
    docCount.textContent = 'KI-Suche läuft...';
    documentationList.innerHTML =
      '<div class="loading-spinner"><div class="spinner"></div><p>Semantische Suche wird durchgeführt...</p></div>';

    const data = await searchDocumentation(query, state.currentCollection, 20, state.selectedDocuments);

    const docFilter = state.selectedDocuments.length > 0
      ? ` (${state.selectedDocuments.length} Dokument${state.selectedDocuments.length > 1 ? 'e' : ''} gefiltert)`
      : '';
    docCount.textContent = `${data.total_results} Ergebnisse für „${query}“${docFilter}`;
    displayDocumentation(data.chunks);
  } catch (error) {
    docCount.textContent = 'Suchfehler';
    {
      const _e = document.createElement("div");
      _e.className = "error-message";
      _e.textContent = `Fehler: ${error.message}`;
      documentationList.replaceChildren(_e);
    }
  }
}
